import React, { createContext, useContext, useEffect, useState } from 'react';
import * as authService from '../services/authService';
import { getMyProfile } from '../services/userService';
import { addToCart as addToCartService } from '../services/cartService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Session expired / token rejected by the server -> sign out locally
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  // Save the signed-in user (state + localStorage) in one place
  const saveUser = (data) => {
    setUser(data);
    localStorage.setItem('mmw_user', JSON.stringify(data));
    if (data?.token) localStorage.setItem('mmw_token', data.token);
  };

  // Merge profile changes (e.g. a new name) into the signed-in user so the
  // header updates immediately
  const updateUser = (changes) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...changes };
      localStorage.setItem('mmw_user', JSON.stringify(next));
      return next;
    });
  };

  // Load the user from localStorage on app start, then refresh the name
  // from the server (fixes the header stuck on "User-1234")
  useEffect(() => {
    const storedUser = localStorage.getItem('mmw_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        getMyProfile()
          .then((p) => {
            if (p?._id) {
              // also picks up admin access granted on the server
              updateUser({
                name: p.name,
                email: p.email,
                phone: p.phone || p.mobile,
                isAdmin: !!p.isAdmin,
                isSuperAdmin: !!p.isSuperAdmin,
                role: p.role
              });
            }
          })
          .catch(() => {});
      } catch (e) {
        localStorage.removeItem('mmw_user');
      }
    }
    setLoading(false);
  }, []);

  // ✅ If a product was queued (Add to Cart while logged out), add it now.
  //    Called right after every successful login/register.
  const addPendingCartItem = async () => {
    const pending = localStorage.getItem('pendingCartItem');
    if (!pending) return;

    try {
      const item = JSON.parse(pending);
      await addToCartService(item);
    } catch (err) {
      console.error('Failed to add pending cart item:', err);
    } finally {
      localStorage.removeItem('pendingCartItem');
    }
  };

  // ✅ Email/Password Login
  const login = async ({ email, password }) => {
    const data = await authService.login({ email, password });

    setUser(data);
    localStorage.setItem("mmw_user", JSON.stringify(data));
    localStorage.setItem("mmw_token", data.token);

    await addPendingCartItem();

    return data;
  };

  // ✅ Register
  const register = async ({ name, email, password, phone }) => {
    const data = await authService.register({ name, email, password, phone });
    setUser(data);
    localStorage.setItem('mmw_user', JSON.stringify(data));
    localStorage.setItem('mmw_token', data.token);

    await addPendingCartItem();

    return data;
  };

  // ✅ Google Login
  const googleLogin = async (idToken) => {
    const data = await authService.googleLogin(idToken);
    setUser(data);
    localStorage.setItem('mmw_user', JSON.stringify(data));
    localStorage.setItem('mmw_token', data.token);

    await addPendingCartItem();

    return data;
  };

  // ✅ Request Phone OTP
  const requestOtp = async (phone) => {
    return await authService.requestOtp(phone);
  };

  // ✅ Verify Phone OTP Login
  const verifyOtpLogin = async ({ phone, otp }) => {
    const data = await authService.verifyOtp({ phone, otp, mode: 'login' });
    saveUser(data);
    await addPendingCartItem();
    return data;
  };

  // ✅ Create account with phone OTP (sends the customer's name)
  const signupWithOtp = async ({ name, phone, otp }) => {
    const data = await authService.verifyOtp({ phone, otp, name, mode: 'signup' });
    saveUser(data);
    await addPendingCartItem();
    return data;
  };

  const devLogin = async (as = 'user') => {
    const data = await authService.devLogin(as);
    saveUser(data);
    await addPendingCartItem();
    return data;
  };

  // ✅ Logout
  const logout = async () => {
    await authService.logout();
    setUser(null);
    localStorage.removeItem('mmw_user');
    localStorage.removeItem('mmw_token');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        googleLogin,
        requestOtp,
        verifyOtpLogin,
        signupWithOtp,
        devLogin,
        updateUser,
        logout
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

// ✅ Custom Hook
export const useAuth = () => useContext(AuthContext);
